/**
 * The player's autopilot — time compression's other half (G44).
 *
 * ## The evidence: one string block, one mechanism
 *
 * These are not three features that happen to sit near each other in
 * `RB.EXE`'s pool. Read at their real offsets they are one sequence, and the
 * pool order is the order they fire in:
 *
 * ```
 * 0x47220  TIME COMPRESS DEACTIVATED.
 * 0x4725c  THE BOMBERS.            \
 * 0x47269  THE ZEPPELINS.           |  the referents `YOU NEAR ` takes
 * 0x47278  THE RECON AIRCRAFT.      |
 * 0x4728c  YOUR BALLOONS.          /
 * ...
 * 0x47440  TIME COMPRESS IS ON.
 * 0x47455  FLIGHT CONTROLS ARE ACTIVE.
 * 0x47471  FLIGHT IS AUTOMATED TO KEEP   <- prefix, no trailing space
 * 0x4748d  YOUR AIRCRAFT IN FORMATION.   <- tail A
 * 0x474a9  YOU NEAR                      <- prefix, trailing space
 * 0x474b3  YOU FLYING A STRAIGHT LINE.   <- tail B
 * 0x474cf  YOUR AIRCRAFT IS
 * 0x474e1  TIME COMPRESS IS NOT AVAILABLE NOW.
 * ```
 *
 * `0x47471` is a **shared prefix with two alternative tails** — `0x4748d` and
 * `0x474b3` — which is how the game gets two sentences out of three strings.
 * (The roadmap and `completeness-inventory.md` both quote the second as
 * *"… KEEPING YOU FLYING A STRAIGHT LINE."*; that wording is in no build of
 * this game. `YOUR AIRCRAFT IS ` at `0x474cf` is a third prefix and belongs to
 * the attitude callouts, not to this.)
 *
 * So: compression engages, the flight is automated, and at the contact the
 * aeroplane is handed back with `FLIGHT CONTROLS ARE ACTIVE.` That is why the
 * port's own module doc for time compression could find no key for an
 * autopilot and no manual entry for one — **there is no separate autopilot.**
 * The Quick Reference Card's HOT KEYS row confirms it from the other side: it
 * lists a time-compress key and no autopilot key at all.
 *
 * ## Which tail, and how the manual settles it
 *
 * The Mission Builder chapter gives the discriminator in one sentence:
 * *"Remember that a flight led by the player won't automatically go to its
 * navigation points if Time Compress is used. Use the briefing text to tell the
 * player where to go."* A player who is leading is therefore flown in a
 * **straight line** — which is exactly why an author has to write the
 * directions into the briefing — and the formation tail is for a player who is
 * *not* leading, holding station on somebody who is navigating.
 *
 * This port's own seniority convention is roster order, stated in
 * `sim-callouts.ts`'s `seniorSurvivor`: *"Roster order is what the FLIGHT
 * ASSIGNMENTS sheet prints as the order of seniority."* Both tails are live
 * under it. The same file notes the port *"always seats the player as leader of
 * his own flight"*, so a patrol or a dogfight takes the straight line; an
 * escort puts the machines he was sent to protect ahead of him in the roster,
 * and there the autopilot keeps him with them, which is both the formation tail
 * and the useful behaviour.
 *
 * ## Where this runs, and what it deliberately does not touch
 *
 * App layer, exactly like time compression itself: this produces an ordinary
 * `FlightInputs` that `scene-loop.ts` substitutes for the player's stick for
 * the frames compression is active. The engine is not told, `entry.ai` stays
 * `null` for the player, and `encounter-hash.ts`'s `aiSnapshot` therefore keeps
 * emitting `null` where it always did — the encounter hash, `SNAPSHOT_VERSION`
 * and the wire are all untouched. Substituting at the frame loop also means the
 * Mission Recorder records what was actually flown and the cockpit's painted
 * stick and rudder move themselves, which is the right percept.
 *
 * `/duel` and `/vcr` never see any of it: both set
 * `timeCompressionAvailable: false`, and the autopilot is gated on the same
 * `allowTimeCompression` flag rather than on a new one. A duel transmits
 * sticks, and an autopilot that ran on one peer and not the other would be a
 * divergence on tick one.
 */

import { holdCourseInputs, routeInputs, defaultFormation, formationStation, headingAngle, type EncounterState, type FlightInputs, type SimState, type Vec3 } from "@red-opus/engine";

/** `RB.EXE` `0x47455`. */
export const FLIGHT_CONTROLS_ACTIVE = "FLIGHT CONTROLS ARE ACTIVE.";
/** `RB.EXE` `0x47471` — the shared opening, which carries no trailing space of its own. */
export const AUTOPILOT_PREFIX = "FLIGHT IS AUTOMATED TO KEEP";
/** `RB.EXE` `0x4748d`. */
export const AUTOPILOT_FORMATION_TAIL = "YOUR AIRCRAFT IN FORMATION.";
/** `RB.EXE` `0x474b3`. */
export const AUTOPILOT_STRAIGHT_TAIL = "YOU FLYING A STRAIGHT LINE.";

/** The two sentences, joined with the single space a two-fragment line needs — the same composition `GUNS_UNJAMMED` uses. */
export const AUTOPILOT_FORMATION_MESSAGE = `${AUTOPILOT_PREFIX} ${AUTOPILOT_FORMATION_TAIL}`;
export const AUTOPILOT_STRAIGHT_MESSAGE = `${AUTOPILOT_PREFIX} ${AUTOPILOT_STRAIGHT_TAIL}`;

export type AutopilotMode = "formation" | "straight";

export function autopilotMessageFor(mode: AutopilotMode): string {
  return mode === "formation" ? AUTOPILOT_FORMATION_MESSAGE : AUTOPILOT_STRAIGHT_MESSAGE;
}

/**
 * The living friendly aeroplane the player is holding station on, or `null`
 * when he is the senior one — the port's roster-order seniority, read the same
 * way `seniorSurvivor` reads it.
 */
export function stationLeaderOf(encounter: EncounterState, playerId: string): SimState | null {
  const friendly = encounter.aircraft.filter((entry) => entry.side === "friendly");
  const index = friendly.findIndex((entry) => entry.id === playerId);
  if (index <= 0) return null;
  const leader = friendly.slice(0, index).find((entry) => entry.status !== "destroyed");
  return leader ? leader.sim : null;
}

export function autopilotModeFor(encounter: EncounterState, playerId: string): AutopilotMode {
  return stationLeaderOf(encounter, playerId) === null ? "straight" : "formation";
}

/**
 * Where the player should be sitting relative to his leader. The station comes
 * from `formations.ts`'s own `formationStation` — the function that placed the
 * flight at its spawn — so the autopilot flies him back to the slot the mission
 * briefed rather than to a number invented here.
 */
function stationFor(encounter: EncounterState, playerId: string, leader: SimState): Vec3 {
  const friendly = encounter.aircraft.filter((entry) => entry.side === "friendly");
  const index = Math.max(friendly.findIndex((entry) => entry.id === playerId), 1);
  return formationStation(defaultFormation(friendly.length), index, leader.position, headingAngle(leader.orientation));
}

/**
 * One frame's worth of stick from the AI, for the player's own aeroplane.
 *
 * Both laws are `combat/route.ts`'s, already exported and — until this — with
 * no consumer in the app at all. That is deliberate reuse rather than economy:
 * they are the laws every *other* aeroplane in the sky is flown by, so the
 * autopilot flies the player's machine exactly the way the AI flies its own.
 */
export function autopilotInputs(encounter: EncounterState, playerId: string, sim: SimState): FlightInputs {
  const leader = stationLeaderOf(encounter, playerId);
  const tunables = sim.config.tunables;
  if (!leader) return holdCourseInputs(sim, tunables);
  // `label` is what a HUD waypoint indicator would print; this station never
  // reaches the HUD, so it takes the banner's own word.
  return routeInputs(sim, { position: stationFor(encounter, playerId, leader), label: "FORMATION" }, tunables);
}

/**
 * What the autopilot is doing, beside `TimeCompressionState` and reconciled in
 * the same breath.
 *
 * `mode` is **latched at engagement** rather than re-read every frame: a
 * wingman whose leader is shot down mid-compression would otherwise flip tails
 * and print a second banner into a three-line message feed that has no expiry
 * (`Fly.tsx`), which is the shape of the standing-stall-warning bug in
 * `sim-callouts.ts`.
 */
export interface AutopilotState {
  readonly engaged: boolean;
  readonly mode: AutopilotMode;
}

export const IDLE_AUTOPILOT: AutopilotState = { engaged: false, mode: "straight" };

export interface AutopilotResult {
  readonly state: AutopilotState;
  /** One of the three verbatim strings above, or `null` when nothing changed this frame. */
  readonly message: string | null;
}

/**
 * The autopilot follows compression exactly: engaged while it is active, handed
 * back the moment it is not. There is no independent toggle, because the
 * original has none.
 */
export function reconcileAutopilot(state: AutopilotState, compressionActive: boolean, mode: AutopilotMode): AutopilotResult {
  if (compressionActive && !state.engaged) {
    return { state: { engaged: true, mode }, message: autopilotMessageFor(mode) };
  }
  if (!compressionActive && state.engaged) {
    return { state: { ...state, engaged: false }, message: FLIGHT_CONTROLS_ACTIVE };
  }
  return { state, message: null };
}
